import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Flex, Typo, Textinput, SwitchRow, theme } from '../../../ui';

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => ($compact ? '6px' : '8px')};
  width: 100%;
`;

const HintText = styled.span`
  font-size: 11px;
  color: ${theme.colors.NEU_600};
  margin-top: 2px;
`;

/**
 * QuizSettingsForm - Shared form component for quiz settings
 * Used by both QuizEdit (modal) and QuizSettingsPanel (side panel)
 *
 * @param {Object} props
 * @param {Object} props.settings - Current settings values
 * @param {Function} props.onChange - Callback when a field changes (field, value)
 * @param {boolean} props.compact - Use compact layout for small spaces (default: false)
 * @param {string} props.inputSize - Size for inputs: 'small' | 'medium' (default: 'medium')
 * @param {boolean} props.isEditing - Whether editing existing quiz (hides name field)
 */
const QuizSettingsForm = ({ settings = {}, onChange, compact = false, inputSize = 'medium', isEditing = false }) => {
  const { t } = useTranslation();

  const handleFieldChange = (field, value) => {
    if (onChange) {
      onChange(field, value);
    }
  };

  return (
    <Flex direction='column' gap={compact ? '12' : '16'} width='100%'>
      {/* Quiz Name - only shown when creating new quiz */}
      {!isEditing && (
        <FormSection $compact={compact}>
          <Typo.SmallText>{t('quiz.quizName')}</Typo.SmallText>
          <Textinput
            value={settings.name || ''}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            placeholder={t('quiz.quizNamePlaceholder') || 'Enter quiz name'}
            size={inputSize}
          />
        </FormSection>
      )}

      {/* Toggle Settings */}
      <FormSection $compact={compact}>
        <SwitchRow
          label={t('quiz.allowToRetake')}
          checked={settings?.repeatable || false}
          onChange={(e) => handleFieldChange('repeatable', e.target.checked)}
          enabledText={t('globals.enabled')}
          disabledText={t('globals.disabled')}
        />
        <SwitchRow
          label={t('quiz.hideAnswers')}
          checked={settings?.hideAnswers || false}
          onChange={(e) => handleFieldChange('hideAnswers', e.target.checked)}
          enabledText={t('globals.enabled')}
          disabledText={t('globals.disabled')}
        />
      </FormSection>

      {/* Welcome Message */}
      <FormSection $compact={compact}>
        <Typo.SmallText>{t('quiz.welcomeMsg')}</Typo.SmallText>
        <Textinput
          isMultiline
          minRows={compact ? 2 : 3}
          maxRows={compact ? 3 : 4}
          value={settings.welcomeText || ''}
          onChange={(e) => handleFieldChange('welcomeText', e.target.value)}
          placeholder={t('quiz.welcomeMsgPlaceholder') || 'Welcome message'}
          size={inputSize}
        />
      </FormSection>

      {/* Description */}
      <FormSection $compact={compact}>
        <Typo.SmallText>{t('globals.description')}</Typo.SmallText>
        <Textinput
          isMultiline
          minRows={compact ? 2 : 3}
          maxRows={compact ? 3 : 4}
          value={settings.descriptionText || ''}
          onChange={(e) => handleFieldChange('descriptionText', e.target.value)}
          placeholder={t('quiz.descriptionPlaceholder') || 'Quiz description'}
          size={inputSize}
        />
      </FormSection>

      {/* Success Message */}
      <FormSection $compact={compact}>
        <Typo.SmallText>{t('quiz.correctMsg')}</Typo.SmallText>
        <Textinput
          value={settings.successText || ''}
          onChange={(e) => handleFieldChange('successText', e.target.value)}
          placeholder={t('quiz.correctMsgPlaceholder') || 'Message when passed'}
          size={inputSize}
        />
      </FormSection>

      {/* Fail Message */}
      <FormSection $compact={compact}>
        <Typo.SmallText>{t('quiz.wrongMsg')}</Typo.SmallText>
        <Textinput
          value={settings.failText || ''}
          onChange={(e) => handleFieldChange('failText', e.target.value)}
          placeholder={t('quiz.wrongMsgPlaceholder') || 'Message when failed'}
          size={inputSize}
        />
      </FormSection>

      {/* Passing Threshold */}
      <FormSection $compact={compact}>
        <Typo.SmallText>{t('quiz.minPercentage')}</Typo.SmallText>
        <Textinput
          type='number'
          value={settings.threshold || ''}
          onChange={(e) => handleFieldChange('threshold', e.target.value)}
          placeholder='80'
          size={inputSize}
        />
        <HintText>{t('quiz.minPercentageHint') || 'Minimum % to pass'}</HintText>
      </FormSection>
    </Flex>
  );
};

export default QuizSettingsForm;
